import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
  ViewContainerRef
} from '@angular/core'
import { combineLatest, Observable, Subscription } from 'rxjs'
import { Page, Result } from 'smartsearch.js'
import { EventService, RoutingService } from '@spartacus/core'
import { SmartSearchService } from '../../services/cms-search/smart-search.service'
import { CmsFacetService } from '../../services/cms-search/cms-facet.service'
import { MbDocumentService } from '../../services/mb-document-service/mb-document.service'
import { URLParam } from 'smartsearch.js/dist/types'
import { MbSpinnerService } from '../../../shared/services/mb-spinner.service'
import { DownloadEvent } from '../shared/gtm-event/download-event.events'
import {
  CmsSearchResultItemWrapperComponent
} from './cms-search-result-item-wrapper/cms-search-result-item-wrapper.component'
import { CmsResultData, ContentResultItem } from '../types'
import { CountryService } from '../../../site-context/facade/country.service'
import { environment } from '../../../../../environments/environment'
import { MbConstants } from '../../../shared/mb-constants'
import { ActivatedRoute } from '@angular/router'

@Component({
  selector: 'mb-cms-search-results',
  templateUrl: './search-results.component.html',
  styleUrls: ['./search-results.component.scss']
})
export class SearchResultsComponent implements OnDestroy, AfterViewInit {

  @ViewChild('searchResults', { read: ViewContainerRef }) searchResults: ViewContainerRef | undefined

  @Input() results$: Observable<Page> | undefined
  @Input() additionalInfo: string | undefined = 'description'
  @Input() isSearchPage: boolean = false
  @Input() searchType: string | undefined
  @Input() globalSearch = false

  readonly certificates = environment.smartSearchCertificates

  queryParams = ''
  query = ''
  paramChanged = false
  subscription = new Subscription()
  results: Page | undefined
  isEmpty = false
  isLastPage = false
  country = ''
  hasTextSearch = false

  constructor (private routingService: RoutingService,
               private activatedRoute: ActivatedRoute,
               private cdRef: ChangeDetectorRef,
               private cmsSearchService: SmartSearchService,
               private cmsFacetService: CmsFacetService,
               private documentService: MbDocumentService,
               private spinnerService: MbSpinnerService,
               private events: EventService,
               private countryService: CountryService) {
  }

  ngAfterViewInit (): void {
    if (this.results$) {
      this.subscription.add(this.results$.subscribe(r => {
        this.results = r
        this.renderResults(r, true)
      }))
      return
    }
    if (this.globalSearch) {
      this.cmsFacetService.setReadonlyFacets([])
    }
    this.subscription.add(combineLatest([this.routingService.getRouterState(), this.countryService.getActive(),
      this.activatedRoute.queryParamMap])
      .subscribe(([state, country, params]) => {
        const queryParams = state.state.queryParams.queryContent ?? ''
        const query = state.state.params['query'] || state.state.queryParams?.search
        this.paramChanged = this.query.toLowerCase() !== String(query).toLowerCase() || this.queryParams !== queryParams
        const countryChanged = this.country !== country
        if (this.paramChanged || countryChanged) {
          const urlParams: URLParam[] = this.cmsSearchService.parseQueryParamsToURLParamsArray(params)
          this.query = query
          this.queryParams = queryParams
          this.isEmpty = false
          this.isLastPage = false
          this.searchInSmartSearch(query, ...urlParams)
        }
        this.country = country
      }))
  }

  async searchInSmartSearch (query: string = '', ...customParams: URLParam[]): Promise<void> {
    if (!query?.length) {
      return
    }
    this.results = await this.cmsSearchService.search(query, this.searchType, ...customParams)
    this.renderResults(this.results, true)
    this.cdRef.detectChanges()
  }

  async loadNextPage () {
    if (!this.results?.hasNext()) {
      return
    }
    await this.results?.next().then(value => {
      this.results = value
      this.renderResults(this.results, false)
    })
  }

  ngOnDestroy (): void {
    this.subscription?.unsubscribe()
  }

  @HostListener('click', ['$event'])
  sendGTMEvent (event: Event): void {
    const target = event?.target as HTMLAnchorElement
    const parent = target?.parentElement as HTMLAnchorElement
    const eventAction = target?.getAttribute('data-event-action')
    const eventActionParent = parent?.getAttribute('data-event-action')
    if (target?.classList?.contains('result-item') && !!eventAction) {
      this.events.dispatch(new DownloadEvent(eventAction, target.getAttribute('data-event-label') ?? ''))
    }
    if (parent?.classList?.contains('result-item') && !!eventActionParent) {
      this.events.dispatch(new DownloadEvent(eventActionParent, parent.getAttribute('data-event-label') ?? ''))
    }
  }

  private renderResults (searchResults?: Page, clear = false): void {
    this.hasTextSearch = (searchResults?.fetchClient?.parameterList?.getOne('query')?.query?.replace('*', '') ?? '')?.trim()?.length > 0
    if (!searchResults && !this.isSearchPage) {
      this.spinnerService.clearMbSpinner()
      return
    }

    if (searchResults) {
      searchResults.searchResults = searchResults.searchResults?.map((result: any) => {
        if (result?.result?.formData) {
          try {
            result.result.formData = JSON.parse(result.result.formData)
          } catch (e) {
          }
        }
        return result
      })

      this.isEmpty = searchResults?.numberOfResults === 0
      this.isLastPage = !searchResults.hasNext()

      if (this.paramChanged && !this.results$) {
        this.cmsFacetService.setFacets(searchResults.facets)
      }

      const searchResultContainer = this.documentService.getElementById('mb_cms_search_results')

      if (!searchResultContainer && !this.isSearchPage) {
        this.spinnerService.clearMbSpinner()
        return
      }

      if (clear && !!searchResultContainer) {
        searchResultContainer.innerHTML = ''
        this.searchResults?.clear()

        if (!this.isSearchPage) {
          this.spinnerService.clearMbSpinner()
        }
      }

      const pageRenderer = this.cmsSearchService.preparedSearch()?.getPageRenderer(searchResults)
      if (pageRenderer) {
        pageRenderer.searchResultTemplate.templateFunction = (data: Result) => this.renderResult(this.mapResult(data as CmsResultData))
        if (searchResultContainer) {
          pageRenderer.renderSearchResultsToHTMLElement(searchResultContainer)
        }
      }
    }
  }

  private renderResult (result: ContentResultItem): string {
    const component = this.searchResults?.createComponent(CmsSearchResultItemWrapperComponent)
    if (!component) {
      return ''
    }
    component.instance.result = result
    component.instance.additionalInfo = this.additionalInfo
    component.hostView.detectChanges()
    return ''
  }

  private mapResult (data: CmsResultData): ContentResultItem {
    const description = (data?.FS_pt_meta_description as any)?.[0] ?? ''
    const keywords = ((data?.formData as any)?.pt_tags?.value ?? []).map((keyword: any) => keyword?.value?.target?.name ?? '')
      .filter((keyword: string) => keyword.length)
    let contentType = keywords.find((tag: any) => tag?.value?.target?.category === 'Content type')?.value?.target?.name
      || (data?.Content_type_keyword as any)?.[0]
      || ''
    contentType = contentType.trim()
    if (this.searchType === environment.smartSearchCertificates || this.searchType === environment.smartSearchClinicalChanges) {
      contentType = this.searchType === environment.smartSearchCertificates ? MbConstants.COA : MbConstants.CCN
    }
    const result: any = {
      seoURL: data?.FS_pt_seoUrl?.pop() || data?.link || '',
      link: data.link ?? '',
      flexType: '',
      description,
      keywords,
      title: (data?.FS_pt_pageTitle as any)?.[0] || data?.title?.[0] || '',
      contentType,
      hasText: (this.additionalInfo === 'description' && description.length) || (this.additionalInfo === 'keyword' && keywords.length),
      isPDF: data?.title?.[0]?.toLowerCase()?.includes('pdf') ?? false,
      faqSnippet: data?.faqSnippetExtracted?.pop() ?? undefined
    }
    if (data?.stills) {
      result.videoData = this.cmsSearchService.mapVideoData(data)
    }
    if (data?.jobField?.length) {
      result.jobData = this.cmsSearchService.mapJobData(data)
    }
    return result
  }
}
